import { DragEvent, useCallback, useState } from 'react';

export function moveItem<T>(items: T[], from: number, to: number): T[] {
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export interface DragItemProps {
  draggable: true;
  onDragStart: (event: DragEvent) => void;
  onDragEnter: (event: DragEvent) => void;
  onDragOver: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
  onDragEnd: () => void;
}

interface DragReorder<T> {
  /** Thứ tự để hiển thị: đã gồm cả vị trí đang kéo dở, nên người dùng thấy hàng dịch chuyển ngay. */
  items: T[];
  draggingIndex: number | null;
  itemProps: (index: number) => DragItemProps;
}

/**
 * Kéo-thả sắp xếp bằng HTML5 Drag and Drop, không thêm thư viện nào.
 *
 * `onCommit` được gọi đúng một lần khi người dùng thả, với thứ tự cuối cùng. Bên gọi có trách nhiệm
 * cập nhật ngay danh sách của mình rồi mới gửi lên máy chủ; nếu chỉ chờ máy chủ trả lời thì danh
 * sách sẽ nhảy về thứ tự cũ trong khoảnh khắc chờ.
 */
export function useDragReorder<T>(source: T[], onCommit: (ordered: T[]) => void): DragReorder<T> {
  const [working, setWorking] = useState<T[] | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const items = working ?? source;

  const itemProps = useCallback(
    (index: number): DragItemProps => ({
      draggable: true,

      onDragStart: event => {
        // Firefox không bắt đầu kéo nếu dataTransfer rỗng.
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));

        setWorking(items);
        setDragging(index);
      },

      onDragEnter: () => {
        if (dragging === null || dragging === index) return;

        setWorking(current => moveItem(current ?? source, dragging, index));
        setDragging(index);
      },

      onDragOver: event => {
        // Không chặn mặc định thì trình duyệt từ chối cho thả.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      },

      onDrop: event => {
        event.preventDefault();

        const ordered = working;

        setWorking(null);
        setDragging(null);

        if (ordered) onCommit(ordered);
      },

      onDragEnd: () => {
        // Thả ra ngoài danh sách nghĩa là hủy: bỏ bản nháp, giữ nguyên thứ tự cũ.
        setWorking(null);
        setDragging(null);
      }
    }),
    [dragging, items, onCommit, source, working]
  );

  return { items, draggingIndex: dragging, itemProps };
}
